import React from 'react';

export interface RoundResultsOverlayProps {
  correctCount: number;
  total: number;
  stability: number;
  score: number;
  year?: number;
  dead?: boolean;
  isShortMode?: boolean;
  primaryLabel: string;
  onPrimary: () => void;
}

const RoundResultsOverlay: React.FC<RoundResultsOverlayProps> = ({
  correctCount,
  total,
  stability,
  score,
  year,
  dead = false,
  isShortMode = false,
  primaryLabel,
  onPrimary,
}) => {
  const calendarYear = year ? 2020 + year - 1 : null;
  const shortCasePassed = isShortMode && correctCount < Math.ceil(total / 2);
  const isApproved = isShortMode ? !shortCasePassed : !dead;
  const verdictStampSrc = isApproved ? '/icons/approved-icon.png' : '/icons/denied-icon.png';
  const verdictText = isShortMode
    ? (isApproved
      ? 'Case dismissed. Humanity presented enough evidence this round.'
      : 'Case passed. Less than half of answers were correct, so the Guardians\' case stands.')
    : (isApproved
      ? 'There is hope here - real hope. Do not waste it. The Council is watching. Come back next year.'
      : 'Vitality has reached zero. The Council has no choice but to deny the application. Try again next time.');

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div
        className="relative w-full max-w-[960px] rounded-[28px] shadow-2xl overflow-hidden"
        style={{
          height: 'auto',
          maxHeight: '85vh',
          overflowY: 'auto',
          background: 'rgba(46, 58, 102, 0.85)',
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        }}
      >
        <div className="absolute left-4 right-4 top-0 h-px bg-gradient-to-r from-transparent via-teal-400 to-transparent drop-shadow-[0_4px_4px_rgba(20,184,166,0.4)]" />

        <div className="relative z-10 flex h-full flex-col items-center px-5 py-2 text-center sm:px-8 sm:py-4">
          <h2
            className="text-[#FFE896]"
            style={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 400, fontSize: 'clamp(30px, 4.6vw, 52px)', lineHeight: 1.2 }}
          >
            EARTH&apos;S {calendarYear ?? ''} VERDICT
          </h2>

          <p
            className="max-w-[880px] text-[#C2DBEE]"
            style={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 400, fontSize: 'clamp(11px, 2.2vw, 18px)', lineHeight: 1.28, fontStyle: 'italic', marginTop: 'clamp(6px, 1.5vh, 20px)' }}
          >
            {verdictText}
          </p>

          <img
            src={verdictStampSrc}
            alt={isApproved ? 'Approved verdict stamp' : 'Denied verdict stamp'}
            className="w-full h-auto object-contain my-4"
            style={{ 
              maxWidth: 'clamp(120px, 38vw, 520px)',
              maxHeight: 'clamp(60px, 16vh, 250px)'
            }}          />


          <div className="mt-auto w-full max-w-[760px]">
            <div className="flex flex-row items-center justify-center gap-8 text-center">
              {!isShortMode && (
                <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 'clamp(10px, 1.8vw, 20px)', lineHeight: 1.2, color: '#A5FFF5' }}>
                  Vitality: <span style={{ color: '#FFE896' }}>{stability}%</span>
                </p>
              )}
              <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 'clamp(10px, 1.8vw, 20px)', lineHeight: 1.2, color: '#A5FFF5' }}>
                Correct: <span style={{ color: '#FFE896' }}>{correctCount}/{total}</span>
              </p>
              <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 'clamp(10px, 1.8vw, 20px)', lineHeight: 1.2, color: '#A5FFF5' }}>
                Score: <span style={{ color: '#FFE896' }}>{score}</span>
              </p>
            </div>
          </div>

          <div className="flex w-full max-w-[760px] justify-center pb-4" style={{ marginTop: 'clamp(8px, 1.5vh, 24px)' }}>
            <button
              onClick={onPrimary}
              className="px-8 py-2.5 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm text-white font-caudex shadow-lg"
            >
              {primaryLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoundResultsOverlay;
